import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import '../styles/Auth.css';

const CompleteRegistration = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await authAPI.verifyRegistrationToken(token);
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (err) {
        setError(err.response?.data?.message || t('auth.verifyLinkExpired'));
      } finally {
        setValidating(false);
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.username.length < 3) {
      setError(t('auth.usernameMinChars'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.passwordMinChars'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDontMatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.completeRegistration({
        token,
        username: formData.username,
        password: formData.password,
        phone: formData.phone || null
      });

      login(response.data.token, response.data.user);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || t('auth.registrationError'));
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <h2>{t('auth.verificationInProgress')}</h2>
            <p>{t('auth.pleaseWait')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
            <h2>{t('auth.invalidLink')}</h2>
            <div className="error-message">{error}</div>
            <p style={{ color: '#666', marginTop: '15px' }}>
              {t('auth.verifyLinkInvalid')}
            </p>
            <Link to="/register" className="btn-primary" style={{ display: 'inline-block', marginTop: '20px' }}>
              {t('auth.restartRegistration')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
          <h2>{t('auth.finalizeRegistration')}</h2>
        </div>

        <div className="success-message" style={{ marginBottom: '20px', textAlign: 'center' }}>
          {t('auth.emailVerified', { email })}
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('auth.username')}</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              minLength="3"
              maxLength="50"
              placeholder={t('auth.usernamePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('auth.phoneOptional')}</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('auth.phonePlaceholder')}
            />
          </div>

          <div className="form-group">
            <label>{t('auth.passwordLabel')}</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
                placeholder={t('auth.minSixChars')}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>{t('auth.confirmPasswordLabel')}</label>
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder={t('auth.confirmPasswordPlaceholder')}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('auth.registering') : t('auth.createAccount')}
          </button>
        </form>

        <p className="auth-link">
          <Link to="/login">{t('auth.alreadyAccount')}</Link>
        </p>
      </div>
    </div>
  );
};

export default CompleteRegistration;
